/**
 * The main/backup routes (PLAN §8.4: "Manual switchover is always available,
 * per production or for a whole node"; D9's per-production "manual only").
 *
 * THE NODES' BUSINESS (`routes.ts` spreads these into `ROUTES`; `@acp/handlers`'
 * rundown API excludes them): a switchover moves the ACTIVE role between the
 * two nodes of a site — the state service serves them itself
 * (`apps/state-service/src/redundancy/routes.ts`). Either node of the pair
 * answers for a production: the one that is active hands over, the standby
 * asks the active to. The cloud serves `switchoverProduction` by relaying it
 * over the site link to the production's node (wave 4c,
 * `apps/cloud-api/src/routes/redundancy.ts`); `switchoverAll`,
 * `updateRedundancy` and `forcePromoteProduction` are node only.
 *
 * The diagnostics (wave 7b-tail C, `pairStatus.ts`) are served by BOTH hosts —
 * `getRedundancyOverview` and `getStandbyPlayout`: a node from its own view and
 * its peer's (a node without a peer answers too: one node, no pair), the cloud
 * from what the site link last carried up.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import {
  forcePromoteRequestWireSchema,
  forcePromoteResultWireSchema,
  redundancyOverviewQueryWireSchema,
  redundancyOverviewResponseWireSchema,
  standbyPlayoutResponseWireSchema,
} from './pairStatus.js';
import { productionPathWireSchema } from './productions.js';
import {
  redundancySettingsResponseWireSchema,
  switchoverAllResponseWireSchema,
  switchoverRequestWireSchema,
  switchoverResultWireSchema,
  updateRedundancyRequestWireSchema,
} from './redundancy.js';
import { BOTH_HOSTS, NODE_ONLY } from './routeHosts.js';
import type { RouteDef } from './routes.js';

export const REDUNDANCY_ROUTES = {
  switchoverProduction: {
    id: 'switchoverProduction',
    method: 'POST',
    path: '/productions/{productionId}/switchover',
    tag: 'redundancy',
    hosts: BOTH_HOSTS,
    summary: 'Switch a production over to the other node',
    description:
      'A clean handover of the ACTIVE role to the other node of the pair (PLAN §8.4): the active mutes (its commands in flight are reported as uncertain), hands its final engine state to the standby, and the standby promotes — seeding each device in D8’s order (the device’s own state, the replicated actual state, a resync that never re-fires one-shot layers) — with the term bumped. ' +
      'Target < 500 ms (PLAN §1.1). Either node answers. Asked of the node holding the claim while its own process is held as a standby (isolated from the peer and every witness), it takes the production on air there at the next term — an operator’s override of "never promote blind". `conflict` when there is no ready standby (the peer is down, or its standby is still syncing) or the production is not running. ' +
      'Asked of the cloud, it is relayed over the site link to the production’s active node (else its site’s main) and answered with that node’s answer; `unavailable` when the node is offline or does not answer in time (wave 4c).',
    params: productionPathWireSchema,
    query: null,
    body: switchoverRequestWireSchema,
    response: switchoverResultWireSchema,
    successStatus: 200,
    permission: 'node.switchover',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'unavailable'],
  },
  switchoverAll: {
    id: 'switchoverAll',
    method: 'POST',
    path: '/nodes/switchover-all',
    tag: 'redundancy',
    hosts: NODE_ONLY,
    summary: 'Move every production active on this node to the other node',
    description:
      'The "switch whole node" button (D9): a switchover of every production whose active process runs on the node answering, one after another — before an image upgrade (PLAN §8.5), or to drain the node. Productions that cannot be moved are listed with the reason; node only.',
    params: null,
    query: null,
    body: switchoverRequestWireSchema,
    response: switchoverAllResponseWireSchema,
    successStatus: 200,
    permission: 'node.switchover',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed'],
  },
  updateRedundancy: {
    id: 'updateRedundancy',
    method: 'PUT',
    path: '/productions/{productionId}/redundancy',
    tag: 'redundancy',
    hosts: NODE_ONLY,
    summary: 'Set a production’s redundancy settings',
    description:
      '`manualOnly: true` turns automatic failover off for this production (D9): a silent active then raises an alarm and waits for an operator’s switchover. Replicated to the other node; node only.',
    params: productionPathWireSchema,
    query: null,
    body: updateRedundancyRequestWireSchema,
    response: redundancySettingsResponseWireSchema,
    successStatus: 200,
    permission: 'node.switchover',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found'],
  },
  getRedundancyOverview: {
    id: 'getRedundancyOverview',
    method: 'GET',
    path: '/redundancy',
    tag: 'redundancy',
    hosts: BOTH_HOSTS,
    summary: 'Main/backup diagnostics: every production with its active and standby node, graded',
    description:
      'Per site: every node (its place in the pair, online, the peer link, the witnesses, the productions it runs active and standby) and every production not ended (its active and standby node, the term, `manualOnly`, the standby’s stream — lag behind the active, last snapshot, gaps, resyncs — the active’s acks, how long it has been out of sync) with a grade `ok` / `warn` / `emergency` and a short reason (docs/redundancy.md, *Seeing it*). ' +
      'A node answers for its own site, live, from its own view and its peer’s (`GET /peer/v1/report`); the cloud answers for every site of the tenant from the nodes’ last health reports (every 30 s), so a node that stopped reporting shows offline. Productions the caller may not read are left out.',
    params: null,
    query: redundancyOverviewQueryWireSchema,
    body: null,
    response: redundancyOverviewResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed'],
  },
  getStandbyPlayout: {
    id: 'getStandbyPlayout',
    method: 'GET',
    path: '/productions/{productionId}/playout/standby',
    tag: 'redundancy',
    hosts: BOTH_HOSTS,
    summary: 'What the standby believes is on air and next',
    description:
      'The standby process’s own playout state (its engine continues from every state the active streams; its devices are muted), so an operator can see that it agrees with the active before relying on it. ' +
      'A node answers from its own standby process, or asks its peer’s (`GET /peer/v1/productions/{id}/playout`); the cloud answers with the last state the standby node reported (`asOf`). `state: null` with a `reason` when there is no standby or it cannot be asked.',
    params: productionPathWireSchema,
    query: null,
    body: null,
    response: standbyPlayoutResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  forcePromoteProduction: {
    id: 'forcePromoteProduction',
    method: 'POST',
    path: '/productions/{productionId}/force-promote',
    tag: 'redundancy',
    hosts: NODE_ONLY,
    summary: 'Emergency activation: force this node’s standby on air',
    description:
      'For the double fault automatic failover refuses to act on (D9: "never promote blind"): the active node AND every witness unreachable, or `manualOnly`. Asked of the STANDBY’s node, it writes the next term naming this node and promotes its process (D8’s seeding). ' +
      'Refused (`conflict`) when the active node answers (switch over instead), when a witness still sees the active (a partition: the active may be driving the devices), or when this node’s standby holds no state. `confirm` must be the production’s name, typed. ' +
      'SPLIT-BRAIN RISK: if the old active is in fact alive and can still reach the devices, both drive them until it sees the higher term and mutes (term fencing still applies; an isolated active that was protected has muted itself already by its lease). Audited on the node and reported to the cloud as a `forced-promotion` alert. Needs `playout.activate` AND `node.switchover` on the production’s studio. Node only.',
    params: productionPathWireSchema,
    query: null,
    body: forcePromoteRequestWireSchema,
    response: forcePromoteResultWireSchema,
    successStatus: 200,
    permission: 'playout.activate',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
} as const satisfies Record<string, RouteDef>;

export type RedundancyRouteId = keyof typeof REDUNDANCY_ROUTES;

export function isRedundancyRouteId(id: string): id is RedundancyRouteId {
  return Object.hasOwn(REDUNDANCY_ROUTES, id);
}
